import { Injectable, Injector, inject } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { Observable, of } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { ConfirmPasswordPopupComponent } from './components/confirm-password-popup/confirm-password-popup.component';
import { CoreHttpClient } from './core-http.client';
import { CorePopupService } from './core-popup.service';
import {
  CoreBackendIDs,
  CoreInstanceInfo,
  CoreLogin,
  CoreMeasure,
  CoreMenu,
  CoreMobiResource,
  CoreMobiResourceType,
  CoreUser,
  CORE_BACKEND,
  Environment,
  ENVIRONMENT,
  MEASURES_ICONS
} from './core.models';
import {
  selectorCoreInstanceInfo,
  selectorCoreInstancesData
} from './core.reducer';
import { CoreUtilsService } from './core-utils';

/**
 * Service responsable des calls API faits par `core`.
 */
@Injectable()
export class CoreApiService {
  private http = inject(CoreHttpClient);
  private injector = inject(Injector);
  private translate = inject(TranslateService);
  private popupService = inject(CorePopupService);
  private store$ = inject<Store<any>>(Store);
  private environment = inject<Environment>(ENVIRONMENT);
  private coreUtilsService = inject(CoreUtilsService);

  /**
   * Se connecter à MOBI,
   * voir [loginParser]{@link #loginParser} pour le parsing des infos retournées par MOBI.
   *
   * **POST** `/rest/v2/;login`
   *
   * @param login Identifiant
   * @param password Mot de passe
   * @param fingerprint fingerprint dans le cas d'une connexion web
   * @param fcmToken FCM Token of user device
   * @param appVersion Installed app version, sent for connection tracking
   */
  login(
    login: string,
    password: string,
    fingerprint?: string,
    fcmToken?: string,
    deviceType?: string,
    userTimezone?: string,
    appVersion?: string
  ): Observable<Partial<CoreLogin>> {
    // Changes below
    // const formData = new FormData();
    // formData.append('login', login);
    // formData.append('password', password);
    // formData.append('fingerprint', fingerprint);
    // formData.append('fcm_token', fcmToken);
    // formData.append('device_type', deviceType);
    const formData = {
      login,
      password,
      fingerprint,
      fcm_token: fcmToken,
      device_type: deviceType,
      user_timezone: userTimezone,
      app_version: appVersion
    };

    const language = { param: 'language', value: this.getNavigatorLanguage() };
    switch (this.injector.get(CORE_BACKEND)) {
      case CoreBackendIDs.SMART:
        return this.http
          .post(`/rest/login?${language.param}=${language.value}`, formData)
          .pipe(map(data => this.loginParser(data)));
      default:
        return this.http
          .post(`/rest/v2/;login?${language.param}=${language.value}`, formData)
          .pipe(map(data => this.loginParser(data)));
    }
  }

  regenerateToken(): Observable<any> {
    const url =
      this.injector.get(CORE_BACKEND) === CoreBackendIDs.SMART
        ? '/rest/users/session/token/refresh'
        : '/api/users/me/refresh-token';
    return this.http
      .post<any>(url, {}, { withMobiCredentials: true })
      .pipe(map(data => data.payload));
  }

  verifyPassword(password: string): Observable<any> {
    return this.http.post<any>(
      `/api/users/me/verify_password`,
      { password: password },
      { withMobiCredentials: true }
    );
  }

  getLanguage(): Promise<any> {
    return Promise.resolve(
      (() => {
        return this.translate.getBrowserLang();
      })()
    );
  }

  getNavigatorLanguage() {
    return window.navigator.language.split('-')[0];
  }

  /**
   * Check SMARTAPP minimum required app version
   *
   * **POST** `/rest/app/version`
   */
  checkAppMinVersion(): Observable<any> {
    const url = '/rest/app/version';
    return this.http.post<any>(url, {}, { withMobiCredentials: false }).pipe(
      map(data => {
        const devType = this.environment.devType.toLowerCase();
        return {
          requiredVersion: data.payload[`${devType}`].app_min_version,
          forceUpdate: data.payload[`${devType}`].force_update
        };
      })
    );
  }

  /**
   * Demande à MOBI de changer son mot de passe.
   *
   * **POST** `/rest/v2/;reset_password`
   *
   * @param login Identifiant
   */
  resetPassword(login: string): Observable<any> {
    const formData = new FormData();
    formData.append('login', login);

    return this.http.post('/rest/v2/;reset_password', formData);
  }

  /**
   * Logout user from the current session
   *
   * **POST** `/rest/logout`
   *
   * Return true if logout success
   */
  logout(): Observable<boolean> {
    const url = '/rest/logout';
    return this.http.post<any>(url, {}, { withMobiCredentials: true }).pipe(
      map(data => {
        return data.status === 200 ? true : false;
      })
    );
  }

  /**
   * Charge les infos de l'utilisateur, voir [userParser]{@link #userParser} pour le parsing des infos retournées par MOBI.
   *
   * **GET** `/api/users/me`
   */
  loadUser(): Observable<CoreUser> {
    return of(this.userParser({}));
    // return this.http.get('/api/users/me', { withMobiCredentials: true }).pipe(
    //   map(data => this.userParser(data))
    // );
  }

  /**
   * Charge le menu de l'utilisateur, voir [menuParser]{@link #menuParser} pour le parsing des infos retournées par MOBI ou SMART.
   *
   * **GET** `/rest/v2/;menu`
   */
  loadMenu(): Observable<CoreMenu> {
    switch (this.injector.get(CORE_BACKEND)) {
      case CoreBackendIDs.SMART:
        return this.http
          .get('/rest/menu', { withMobiCredentials: true })
          .pipe(map(data => this.newMenuParser(data)));
      default:
        return this.http
          .get('/rest/v2/;menu', { withMobiCredentials: true })
          .pipe(map(data => this.menuParser(data)));
    }
  }

  /**
   * Load instance info, see [instanceInfoParser]{@link #instanceInfoParser} for parsing info returned by MOBI.
   *
   * **GET** `/rest/v2/;instance_info`
   */
  loadInstanceInfo(useCredentials = false): Observable<CoreInstanceInfo> {
    switch (this.injector.get(CORE_BACKEND)) {
      case CoreBackendIDs.SMART: {
        let instanceId: string;
        let productId: string;
        let isQRcode = false;
        const devTypeKey = this.environment.devType.toLowerCase();
        this.store$
          .pipe(
            select(selectorCoreInstancesData),
            filter(instance => !!instance)
          )
          .subscribe(instance => {
            instanceId = instance[`${devTypeKey}`].code || '';
            productId = instance[`${devTypeKey}`].product_identifier || '';
            if (productId && productId !== '') {
              isQRcode = true;
            }
          });
        return this.http
          .post(`/rest/instance_info`, {
            instance_code: instanceId,
            instance_env: devTypeKey,
            product_identifier: productId
          })
          .pipe(
            map(data => {
              const parsedData = this.instanceInfoParser(data, isQRcode);
              localStorage.setItem('instance_language', parsedData.language);
              return parsedData;
            })
          );
      }

      default:
        return this.http
          .get('/rest/v2/;instance_info', {
            withMobiCredentials: useCredentials
          })
          .pipe(
            map(data => {
              const parsedData = this.instanceInfoParser(data);
              localStorage.setItem('instance_language', parsedData.language);
              return parsedData;
            })
          );
    }
  }

  /**
   * Fetch document content from web service and pass it to [documentParser]{@link #documentParser}
   * @param data, it includes href to call API
   *
   * @returns Observable<void>
   */
  fetchDocument(href: any): Observable<any> {
    if (href) {
      // return of(
      //   this.coreUtilsService.documentParser(this.mockedDocumentData)
      // );
      return this.http
        .get(href, { withMobiCredentials: true })
        .pipe(map(data => this.coreUtilsService.documentParser(data)));
    }
  }

  /**
   * Charge les infos d'une mesure.
   *
   * @param href href de la mesure
   */
  loadMeasure(href: string): Observable<CoreMeasure> {
    return this.http
      .get(href, { withMobiCredentials: true })
      .pipe(map(data => this.measureParser(data)));
  }

  /**
   * Envoie un token FCM à MOBI.
   *
   * **POST** `/api/users/me/firebase_tokens`
   *
   * @param fcm Le token FCM
   * @param label Le titre du token
   */
  sendFCMTokenToAPI(fcm: string, label: string): Observable<any> {
    return this.http.post(
      '/api/users/me/firebase_tokens',
      {
        fcm,
        label
      },
      {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        withMobiCredentials: true
      }
    );
  }

  /**
   * Refresh FCM Token: If user change default device
   *
   * **POST** `/rest/users/fcm/token/refresh`
   */
  refreshFCMToken(tokenInfo): Observable<any> {
    const url = '/rest/users/fcm/token/refresh';
    return this.http
      .post<any>(url, { fcm_token: tokenInfo }, { withMobiCredentials: true })
      .pipe(
        map(data => {
          return data;
        })
      );
  }

  /**
   * Parse les infos d'une mesure.
   *
   * @param data Les données retournées par MOBI
   */
  measureParser(data: any): CoreMeasure {
    const meta = data.meta || {};
    return <CoreMeasure>{
      icon: MEASURES_ICONS[meta.sub_type] || meta.icon || '',
      color: meta.color || '',
      title: meta.title || '',
      lastEntry: (meta.last_activity && meta.last_activity.time) || '',
      lastEntryLabel:
        meta.last_activity && meta.last_activity.time
          ? this.translate.instant('MEASURE.LAST_ENTRY', {
              time: moment(meta.last_activity.time).fromNow(true)
            })
          : this.translate.instant('MEASURE.NO_ENTRY'),
      activityId: '',
      activityModelId: meta.abspath || '',
      device:
        (meta.device &&
          meta.device.device_name &&
          meta.device.title &&
          meta.device.use_extra && {
            name: meta.device.device_name,
            title: meta.device.title,
            useExtra: meta.device.use_extra
          }) ||
        void 0,
      fields: (meta.model_keys || []).map(model => ({
        key: model.value || '',
        unit: model.unit || '',
        title: model.label || '',
        minValue: model.min_value || void 0,
        maxValue: model.max_value || void 0,
        minValueIndicative: model.min_value_indicative || void 0,
        maxValueIndicative: model.max_value_indicative || void 0
      })),
      subType: meta.sub_type || '',
      isManualMeasure: meta.is_manual_measure || false,
      uuid: data.uuid || meta.uuid || ''
    };
  }

  confirmLogin(): Observable<any> {
    const obs = new Observable(observer => {
      this.store$
        .pipe(select(selectorCoreInstanceInfo), take(1))
        .subscribe(instanceInfo => {
          this.popupService
            .create(ConfirmPasswordPopupComponent, {
              inputs: { color: instanceInfo.appColor }
            })
            .then(component => {
              component.validated.subscribe(value => {
                if (value === 'cancel') {
                  this.popupService.dismiss();
                  observer.complete();
                } else {
                  this.verifyPassword(value).subscribe(
                    response => {
                      observer.next(response);
                      observer.complete();
                    },
                    error => {
                      observer.next(error);
                      observer.complete();
                    }
                  );
                }
              });
            });
        });
    });
    return obs;
  }

  /**
   * Parse les infos de login retournées par MOBI ([exemple]{@link /core/additional-documentation/exemples-d'api-(mobi)/login.html}).
   *
   * @param data Les données retournées par MOBI
   */
  private loginParser(data: any): Partial<CoreLogin> {
    // Extract user ID from JWT token if available
    const userIdFromToken = this.coreUtilsService.extractUserIdFromToken(
      data.iauth
    );

    return {
      iAuth: data.iauth || '',
      userId:
        userIdFromToken || (data.user_id && parseInt(data.user_id, 10)) || 0,
      userMustChangePassword: data.user_must_change_password || false,
      uuid: data.uuid || '',
      language: data.user_language || '',
      rightToLeft: data.user_language ? data.user_language === 'ar' : false,
      tokenValidationCheckFrequency:
        data.token_validation_check_frequency || '',
      tokenWarningExpirationDuration:
        data.token_warning_expiration_duration || '',
      isNewDevice: data.is_new_device,
      popupNewDeviceMessage: data.popup_new_device_message,
      isDisplayQuickTour: data.is_display_quicktour || false
    };
  }

  /**
   * Parse les infos de l'utilisateur retournées par MOBI
   * ([exemple]{@link /core/additional-documentation/exemples-d'api-(mobi)/infos-utilisateur.html}).
   *
   * @param data Les données retournées par MOBI
   */
  private userParser(data: any): CoreUser {
    const meta = data.meta || {};
    const personal = meta.personal || {};
    const configurations = meta.configurations || {};
    const dateSettings = configurations.date_settings || {};
    // set user data for google Analytics
    return {
      uuid: meta.uuid || '',
      personal: {
        birthdate: personal.birthdate || '',
        email: personal.email || '',
        nickname: personal.nickname || '',
        presentation: personal.presentation || ''
      },
      configuration: {
        dateSettings: {
          dateFormat: dateSettings.dateformat || 'DD/MM/YYYY',
          timeFormat: dateSettings.timeformat || '%l:%M %p',
          timezone: dateSettings.timezone || 'Europe/Paris'
        },
        displayMeasuresInExtra:
          configurations.display_measures_in_extra || false,
        groups: configurations.groups || [],
        isPro: configurations.is_pro || false,
        language: configurations.language || 'en',
        proIsAllowedToControlMail:
          configurations.pro_is_allowed_to_control_mail || false,
        rightToLeft: configurations.right_to_left || false,
        registrationDate: configurations.ctime || ''
      }
    };
  }

  /**
   * Parse les infos de l'instance retournées par MOBI
   * ([exemple]{@link /core/additional-documentation/exemples-d'api-(mobi)/infos-instance.html}).
   *
   * @param data Les données retournées par MOBI
   */
  private instanceInfoParser(data: any, isQRScanned = false): CoreInstanceInfo {
    if (data.instance_url) {
      localStorage.setItem('instanceUrl', data.instance_url);
    }
    return {
      instanceColor: data.app_v3_color || '#647aec',
      // Raw value only. The theme-aware adjustment is applied once, centrally,
      // in the LOAD_INSTANCE_INFO_SUCCESS reducer so both API parsers stay in
      // sync and the logic is not duplicated per-parser.
      appColor: data.app_v3_color || '#647aec',
      appVersionV3: data.app_version_v3 || '',
      defaultCategoryColor: data.category_default_color || '#647aec',
      defaultFactsheetColor: data.factsheet_default_color || '#647aec',
      defaultFormColor: data.form_default_color || '#647aec',
      defaultMeasureColor: data.measure_default_color || '#647aec',
      defaultEDocsColor: data.edoc_default_color || '#647aec',
      displayActionButton: data.display_action_button || false,
      logo: data.instance_logo_url || '',
      title: data.title || '',
      url: data.instance_url || '',
      language: data.instance_default_language || '',
      instance_code: data.instance_code,
      isQR: isQRScanned
    };
  }

  /**
   * Parse les infos du menu retournées par MOBI ([exemple]{@link /core/additional-documentation/exemples-d'api-(mobi)/menu.html}).
   *
   * @param data Les données retournées par MOBI
   */
  private menuParser(data: any): CoreMenu {
    return {
      color: void 0,
      instanceLogo: void 0,
      mainButtons: (data.user_menu || [])
        .map(item => {
          item.notifications = 0; // Disable the display of the number of notifications
          const convertButton = (type: string, notifications: number) => {
            const base = { iconColor: '#2e2e2e', notifications };
            switch (type) {
              case 'timeline':
                return { ...base, id: 'home' };
              case 'mail':
                return { ...base, id: 'messages' };
              case 'dashboard':
                return { ...base, id: 'myMeasures' };
              case 'measures':
                return { ...base, id: 'measures' };
              case 'patient-events':
                return { ...base, id: 'events' };
              case 'myFiles':
                return { ...base, id: 'myFiles' };
              default:
                return void 0;
            }
          };

          return convertButton(item.code, item.notifications);
        })
        .filter(item => !!item),
      secondaryButtonGroups: (data.website_menu || [])
        .map(item => ({
          title: item.title,
          buttons: (item.sub_items || [])
            .map(subItem => {
              const convertResource = (
                type: string,
                href: string
              ): CoreMobiResource => {
                switch (type) {
                  case 'form':
                    return { type: CoreMobiResourceType.FORM, href };
                  case 'category':
                    return { type: CoreMobiResourceType.CATEGORY, href };
                  case 'forms-container':
                    return { type: CoreMobiResourceType.CATEGORY, href };
                  case 'fiche':
                  case 'factsheet':
                    return { type: CoreMobiResourceType.FACTSHEET, href };
                  case 'quizz':
                    return { type: CoreMobiResourceType.QUIZZ, href };
                  case 'chatbot':
                    return { type: CoreMobiResourceType.CHATBOT, href };
                  case 'body-schema-pain':
                    return { type: CoreMobiResourceType.BODY_SCHEMA, href };
                  default:
                    return void 0;
                }
              };

              const resource = convertResource(
                subItem.sub_type || '',
                subItem.href || ''
              );

              return resource
                ? {
                    label: subItem.title,
                    resource
                  }
                : void 0;
            })
            .filter(subItem => !!subItem)
        }))
        .filter(item => item.buttons.length)
    };
  }

  /**
   * @ignore Universal parser, can work with Smart and Mobi data
   */
  private newMenuParser(data: any): CoreMenu {
    return {
      color: data.payload.color || void 0,
      instanceLogo: data.payload.logo || void 0,
      mainButtons: (data.payload.main_buttons || data.user_menu || [])
        .map(item => {
          item.notifications = 0; // Disable the display of the number of notifications
          const convertButton = (type: string, notifications: number) => {
            const base = { icon_color: '#2e2e2e', notifications };
            switch (type) {
              case 'home':
                return { ...base, id: 'home' };
              case 'mail':
                return { ...base, id: 'messages' };
              case 'dashboard':
                return { ...base, id: 'myMeasures' };
              case 'measures':
                return { ...base, id: 'measures' };
              case 'patient-events':
                return { ...base, id: 'events' };
              case 'eDiary':
                return { ...base, id: 'eDiary' };
              case 'myFiles':
                return {
                  ...base,
                  id: 'myFiles',
                  fileSize: item.file_size || 10,
                  attachmentTypes: item.attachment_types || []
                };
              default:
                return void 0;
            }
          };

          return convertButton(item.type || item.code, item.notifications);
        })
        .filter(item => !!item),
      secondaryButtonGroups: (
        data.payload.secondary_buttons ||
        data.website_menu ||
        []
      )
        .map(item => ({
          title: item.title,
          buttons: (item.buttons || item.sub_items || [])
            .map(btnItem => {
              const convertResource = (
                type: string,
                href: string
              ): CoreMobiResource => {
                switch (type) {
                  case 'form':
                    return { type: CoreMobiResourceType.FORM, href };
                  case 'category':
                    return { type: CoreMobiResourceType.CATEGORY, href };
                  case 'forms-container':
                    return { type: CoreMobiResourceType.CATEGORY, href };
                  case 'fiche':
                  case 'factsheet':
                    return { type: CoreMobiResourceType.FACTSHEET, href };
                  case 'quizz':
                    return { type: CoreMobiResourceType.QUIZZ, href };
                  case 'chatbot':
                    return { type: CoreMobiResourceType.CHATBOT, href };
                  case 'body-schema-pain':
                    return { type: CoreMobiResourceType.BODY_SCHEMA, href };
                  default:
                    return void 0;
                }
              };

              const resource = convertResource(
                btnItem.type || btnItem.sub_type || '',
                btnItem.href || ''
              );

              return resource
                ? {
                    label: btnItem.label || '',
                    color: btnItem.color || '',
                    // label: btnItem.label || btnItem.title,
                    resource
                  }
                : void 0;
            })
            .filter(btnItem => !!btnItem)
        }))
        .filter(item => item.buttons.length)
    };
  }

  /**
   * @ignore
   * */
  private mockedDocumentData = {
    payload: {
      file_name: 'eConsent_Test-2022-05-03.pdf',
      file_type: 'pdf',
      content:
        'JVBERi0xLjIgCjkgMCBvYmoKPDwKPj4Kc3RyZWFtCkJULyAzMiBUZiggIFlPVVIgVEVYVCBIRVJFICAgKScgRVQKZW5kc3RyZWFtCmVuZG9iago0IDAgb2JqCjw8Ci9UeXBlIC9QYWdlCi9QYXJlbnQgNSAwIFIKL0NvbnRlbnRzIDkgMCBSCj4+CmVuZG9iago1IDAgb2JqCjw8Ci9LaWRzIFs0IDAgUiBdCi9Db3VudCAxCi9UeXBlIC9QYWdlcwovTWVkaWFCb3ggWyAwIDAgMjUwIDUwIF0KPj4KZW5kb2JqCjMgMCBvYmoKPDwKL1BhZ2VzIDUgMCBSCi9UeXBlIC9DYXRhbG9nCj4+CmVuZG9iagp0cmFpbGVyCjw8Ci9Sb290IDMgMCBSCj4+CiUlRU9G'
    }
  };

  /**
   * @ignore
   */
  private newMockedMenu(): Observable<any> {
    return of({
      color: '#647aec',
      instanceLogo:
        // tslint:disable-next-line:max-line-length
        'PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0idXRmLTgiPz4KPCEtLSBHZW5lcmF0b3I6IEFkb2JlIElsbHVzdHJhdG9yIDIzLjAuNCwgU1ZHIEV4cG9ydCBQbHVnLUluIC4gU1ZHIFZlcnNpb246IDYuMDAgQnVpbGQgMCkgIC0tPgo8c3ZnIHZlcnNpb249IjEuMSIgaWQ9IkNhbHF1ZV8xIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiB4PSIwcHgiIHk9IjBweCIKCSB2aWV3Qm94PSIwIDAgMTY5IDEzNyIgc3R5bGU9ImVuYWJsZS1iYWNrZ3JvdW5kOm5ldyAwIDAgMTY5IDEzNzsiIHhtbDpzcGFjZT0icHJlc2VydmUiPgo8c3R5bGUgdHlwZT0idGV4dC9jc3MiPgoJLnN0MHtkaXNwbGF5Om5vbmU7fQoJLnN0MXtkaXNwbGF5OmlubGluZTt9Cgkuc3Qye2ZpbGw6IzJCMkMyQzt9Cgkuc3Qze2ZpbGw6IzZCM0NEOTt9Cgkuc3Q0e2ZpbGw6IzdBNTBERDt9Cgkuc3Q1e2ZpbGw6Izg5NjNFMTt9Cgkuc3Q2e2ZpbGw6Izk4NzdFNDt9Cgkuc3Q3e2ZpbGw6I0E2OEFFODt9Cgkuc3Q4e2Rpc3BsYXk6aW5saW5lO2ZpbGw6IzZCM0NEOTt9Cgkuc3Q5e2ZvbnQtZmFtaWx5OidBcXVhR3JvdGVzcXVlJzt9Cgkuc3QxMHtmb250LXNpemU6MTYuODQ5OXB4O30KCS5zdDExe2ZpbGw6IzgwODA4MDtzdHJva2U6IzgwODA4MDtzdHJva2Utd2lkdGg6MS4xOTk2O3N0cm9rZS1taXRlcmxpbWl0OjEwO30KCS5zdDEye2ZpbGw6I0M3QzdDNzt9Cgkuc3QxM3tmaWxsOiNFOUU5RTk7fQoJLnN0MTR7ZmlsbDojRkZGRkZGO30KCS5zdDE1e29wYWNpdHk6MC45O2ZpbGw6IzZCM0NEOTtlbmFibGUtYmFja2dyb3VuZDpuZXcgICAgO30KCS5zdDE2e29wYWNpdHk6MC45O30KCS5zdDE3e29wYWNpdHk6MC45O2ZpbGw6I0NFQTE5MDtlbmFibGUtYmFja2dyb3VuZDpuZXcgICAgO30KCS5zdDE4e2ZpbGw6I0Y0RjZGODt9Cjwvc3R5bGU+CjxnIGNsYXNzPSJzdDAiPgoJPGcgY2xhc3M9InN0MSI+CgkJPGc+CgkJCTxjaXJjbGUgY2xhc3M9InN0MiIgY3g9IjE4NC43IiBjeT0iODMuNiIgcj0iNC42Ii8+CgkJCTxwYXRoIGNsYXNzPSJzdDIiIGQ9Ik0xMTQuMSw4NC42TDExNC4xLDg0LjZjLTAuMSwwLTAuMSwwLTAuMS0wLjFjLTAuMSwwLTAuMi0wLjEtMC4zLTAuMWMtMC4yLTAuMS0wLjQtMC4yLTAuNi0wLjIKCQkJCWMtMC40LTAuMi0wLjgtMC4zLTEuMi0wLjRsMCwwYy0yLjEtMC43LTQuMy0xLjEtNi42LTEuMWMtMy4xLDAtNS45LDAuNi04LjYsMS45bDAsMGwwLDBjLTEuMSwwLjUtMi4xLDEuMS0zLjEsMS44CgkJCQljLTAuMy0xLjYtMS41LTIuOS0zLTMuNWMtMS4xLTAuNC0yLjQtMC41LTMuOCwwLjNjLTEuNywwLjktMi42LDIuNy0yLjYsNC41djMuMnY4LjR2NC40djEwLjh2MTEuN2MwLDIuMiwxLjQsNC4yLDMuNSw0LjgKCQkJCWMxLjEsMC4zLDIuMiwwLjEsMy4yLTAuM2MxLjYtMC43LDIuOC0yLjMsMi44LTQuMnYtNS40di02Ljl2LTEwLjZ2LTEuOWMwLTIsMC44LTMuOSwyLjEtNS40YzEuMi0xLjMsMi43LTIuNCw0LjMtMy4yCgkJCQljMC45LTAuNCwxLjgtMC43LDIuOC0wLjljMC4yLDAsMC41LTAuMSwwLjgtMC4xYzAuMSwwLDAuMywwLDAuNC0wLjFjMC40LDAsMC45LTAuMSwxLjMtMC4xYzEuMiwwLDIuNCwwLjIsMy42LDAuNgoJCQkJYzEuMiwwLjQsMi42LDAuMSwzLjUtMC45bDIuNi0yLjhDMTE2LjUsODcuNSwxMTYuMSw4NS41LDExNC4xLDg0LjZ6Ii8+CgkJCTxwYXRoIGNsYXNzPSJzdDIiIGQ9Ik0xODQuNyw5MS41Yy0yLjUsMC00LjYsMi4xLTQuNiw0LjZ2MzAuNGMwLDIuNSwyLjEsNC42LDQuNiw0LjZzNC42LTIuMSw0LjYtNC42Vjk2LjEKCQkJCUMxODkuMyw5My42LDE4Ny4yLDkxLjUsMTg0LjcsOTEuNXoiLz4KCQkJPHBhdGggY2xhc3M9InN0MiIgZD0iTTcyLjEsOTcuM2MtMS4zLTMtMy01LjUtNS4xLTcuN2MtMi4yLTIuMi00LjctMy45LTcuNi01LjJzLTYtMS45LTkuMy0xLjlzLTYuNCwwLjYtOS4zLDEuOQoJCQkJYy0yLjksMS4zLTUuNCwzLTcuNiw1LjJjLTIuMSwyLjItMy44LDQuNy01LjEsNy43Yy0xLjMsMy0xLjksNi4xLTEuOSw5LjRjMCwzLjMsMC42LDYuNSwxLjksOS40czMsNS41LDUuMSw3LjcKCQkJCWMyLjIsMi4yLDQuNywzLjksNy42LDUuMmMyLjEsMC45LDQuMywxLjUsNi43LDEuOGMwLjksMC4xLDEuNywwLjEsMi42LDAuMWMyLjgsMCw1LjQtMC40LDcuOS0xLjNjMi40LTAuOSw0LjctMi4xLDYuNy0zLjd2MC43CgkJCQljMCwyLjUsMi4xLDQuNiw0LjYsNC42czQuNi0yLjEsNC42LTQuNnYtMTEuNHYtOC40Qzc0LDEwMy40LDczLjMsMTAwLjMsNzIuMSw5Ny4zeiBNNjAuNCwxMTcuMWMtMS4zLDEuMy0yLjksMi40LTQuNiwzLjIKCQkJCWMtMS44LDAuOC0zLjcsMS4yLTUuNywxLjJjLTAuOSwwLTEuOC0wLjEtMi42LTAuMmMtMS4xLTAuMi0yLjEtMC41LTMuMS0wLjljLTEuOC0wLjgtMy4zLTEuOS00LjYtMy4yYy0xLjMtMS4zLTIuNC0yLjktMy4xLTQuNwoJCQkJYy0wLjgtMS44LTEuMi0zLjctMS4yLTUuN2MwLTIuMSwwLjQtNCwxLjItNS44YzAuOC0xLjgsMS44LTMuNCwzLjEtNC43YzEuMy0xLjMsMi45LTIuNCw0LjYtMy4yYzEuOC0wLjgsMy43LTEuMiw1LjctMS4yCgkJCQlzMy45LDAuNCw1LjcsMS4yYzEuOCwwLjgsMy4zLDEuOSw0LjYsMy4yYzEuMywxLjMsMi40LDIuOSwzLjEsNC43YzAuOCwxLjgsMS4yLDMuNywxLjIsNS44YzAsMi0wLjQsMy45LTEuMiw1LjcKCQkJCWMtMC40LDEtMC45LDEuOS0xLjUsMi43QzYxLjYsMTE1LjksNjEsMTE2LjUsNjAuNCwxMTcuMXoiLz4KCQkJPHBhdGggY2xhc3M9InN0MiIgZD0iTTE2NC41LDYwLjVjLTIuNiwwLTQuNywyLjEtNC43LDQuN3Y4LjJsMC4xLDE0LjJjLTIuMS0xLjYtNC40LTIuOC02LjktMy43cy01LjItMS4zLTgtMS4zCgkJCQljLTMuNCwwLTYuNSwwLjYtOS41LDEuOWMtMi45LDEuMy01LjUsMy03LjcsNS4ycy0zLjksNC44LTUuMiw3LjdjLTAuOCwxLjktMS4zLDMuOS0xLjYsNS45Yy0wLjIsMS4yLTAuMywyLjMtMC4zLDMuNgoJCQkJYzAsMy40LDAuNiw2LjUsMS45LDkuNWMxLjMsMi45LDMsNS41LDUuMiw3LjdzNC44LDMuOSw3LjcsNS4yYzAuMSwwLDAuMiwwLjEsMC4zLDAuMWMyLjgsMS4yLDUuOSwxLjgsOS4xLDEuOAoJCQkJYzMuNCwwLDYuNS0wLjYsOS41LTEuOWMyLjktMS4zLDUuNS0zLDcuNy01LjJzMy45LTQuOCw1LjItNy43czEuOS02LjEsMS45LTkuNXYtMC4xVjczLjV2LTguM0MxNjkuMiw2Mi42LDE2Ny4xLDYwLjUsMTY0LjUsNjAuNQoJCQkJeiBNMTU5LjcsMTA2LjljMCwyLTAuNCwzLjktMS4yLDUuN3MtMS45LDMuNC0zLjIsNC43cy0yLjksMi40LTQuNywzLjJzLTMuNywxLjEtNS43LDEuMXMtMy45LTAuNC01LjctMS4xCgkJCQljLTEuMi0wLjUtMi4zLTEuMi0zLjQtMmMtMC41LTAuNC0wLjktMC44LTEuNC0xLjJjLTEuMy0xLjMtMi40LTIuOS0zLjItNC43cy0xLjEtMy43LTEuMS01LjdjMC0xLjIsMC4yLTIuNCwwLjQtMy42CgkJCQljMC4yLTAuNywwLjQtMS41LDAuNy0yLjJjMC44LTEuOCwxLjgtMy40LDMuMi00LjdjMS4zLTEuMywyLjktMi40LDQuNy0zLjJzMy43LTEuMSw1LjctMS4xczMuOSwwLjQsNS43LDEuMQoJCQkJYzEuOCwwLjgsMy40LDEuOCw0LjcsMy4yYzEuMywxLjMsMi40LDIuOSwzLjIsNC43QzE1OS4zLDEwMi45LDE1OS43LDEwNC44LDE1OS43LDEwNi45TDE1OS43LDEwNi45eiIvPgoJCQk8cGF0aCBjbGFzcz0ic3QyIiBkPSJNMjQ2LjQsOTcuOGMwLTAuMS0wLjEtMC4yLTAuMS0wLjNjLTEuMy0yLjktMy01LjUtNS4yLTcuN3MtNC44LTMuOS03LjctNS4yYy0yLjktMS4zLTYuMS0xLjktOS40LTEuOQoJCQkJYy0zLjQsMC02LjUsMC42LTkuNCwxLjljLTIuOSwxLjMtNS41LDMtNy43LDUuMnMtMy45LDQuOC01LjIsNy43Yy0wLjUsMS4yLTAuOSwyLjUtMS4yLDMuOGMtMC40LDEuOC0wLjcsMy43LTAuNyw1LjYKCQkJCWMwLDMuNCwwLjYsNi41LDEuOSw5LjRjMS4zLDIuOSwzLDUuNSw1LjIsNy43czQuOCwzLjksNy43LDUuMmMxLjEsMC41LDIuMywwLjgsMy40LDEuMWMxLjksMC41LDMuOSwwLjgsNiwwLjgKCQkJCWMzLjQsMCw2LjUtMC42LDkuNC0xLjljMi45LTEuMyw1LjUtMyw3LjctNS4yczMuOS00LjgsNS4yLTcuN2MwLjMtMC43LDAuNS0xLjQsMC44LTIuMWMwLjctMi4zLDEuMS00LjgsMS4xLTcuNAoJCQkJQzI0OC4yLDEwMy43LDI0Ny42LDEwMC42LDI0Ni40LDk3Ljh6IE0yMzQuNSwxMTcuNGMtMS4zLDEuMy0yLjksMi40LTQuNywzLjJjLTEuOCwwLjgtMy43LDEuMS01LjcsMS4xcy0zLjktMC40LTUuNy0xLjEKCQkJCWMtMC4xLTAuMS0wLjItMC4xLTAuNC0wLjJjLTEuNi0wLjctMy4xLTEuNy00LjQtM2MtMS4zLTEuMy0yLjQtMi45LTMuMi00LjdzLTEuMi0zLjctMS4yLTUuN3MwLjQtMy45LDEuMi01LjZ2LTAuMQoJCQkJYzAuOC0xLjgsMS45LTMuNCwzLjItNC43czIuOS0yLjQsNC43LTMuMmMxLjgtMC44LDMuNy0xLjEsNS43LTEuMXMzLjksMC40LDUuNywxLjFjMS44LDAuOCwzLjQsMS44LDQuNywzLjIKCQkJCWMwLjQsMC40LDAuOCwwLjgsMS4xLDEuM2MwLjgsMSwxLjUsMi4yLDIsMy40YzAuOCwxLjgsMS4xLDMuNywxLjEsNS43cy0wLjQsMy45LTEuMSw1LjdjLTAuMiwwLjYtMC41LDEuMS0wLjgsMS42CgkJCQlDMjM2LjIsMTE1LjQsMjM1LjQsMTE2LjUsMjM0LjUsMTE3LjR6Ii8+CgkJCTxwYXRoIGNsYXNzPSJzdDIiIGQ9Ik05LjgsOTMuNWMwLjksMC40LDEuNywwLjksMi40LDEuNWMxLjgsMS40LDQuMiwxLjUsNS45LDAuMWwwLDBjMi40LTEuOSwyLjUtNS41LDAuMS03LjQKCQkJCWMtMS4zLTEtMi43LTItNC4zLTIuN0MxMC42LDgzLjMsNyw4Mi41LDMsODIuNWMtMy4yLDAtNi4xLDAuNi05LDEuN2MtMC4xLDAuMS0wLjMsMC4xLTAuNCwwLjJjLTMsMS4zLTUuNSwzLTcuOCw1LjIKCQkJCWMtMi4yLDIuMi00LDQuOC01LjMsNy43Yy0wLjQsMC44LTAuNywxLjctMC45LDIuNmMtMC42LDIuMi0xLDQuNS0xLDYuOGMwLDMuNCwwLjYsNi41LDEuOSw5LjVzMyw1LjUsNS4zLDcuNwoJCQkJYzIuMiwyLjIsNC44LDQsNy44LDUuMmMzLDEuMyw2LjEsMS45LDkuNCwxLjljMy4zLDAsNi4zLTAuNiw5LjItMS44YzAuNi0wLjIsMS4yLTAuNSwxLjctMC44YzEuNS0wLjgsMi45LTEuNyw0LjItMi43CgkJCQljMi40LTEuOSwyLjMtNS41LTAuMS03LjRjLTEuNy0xLjMtNC4xLTEuMy01LjgsMGMwLDAtMC4xLDAtMC4xLDAuMWMtMC43LDAuNi0xLjYsMS4xLTIuNCwxLjVjLTIsMS00LjIsMS42LTYuNiwxLjYKCQkJCWMtMiwwLTQtMC40LTUuOC0xLjJzLTMuNC0xLjktNC44LTMuMmMtMS40LTEuMy0yLjQtMi45LTMuMi00LjdjLTAuOC0xLjgtMS4yLTMuNy0xLjItNS43czAuNC0zLjksMS4yLTUuNwoJCQkJYzAuMi0wLjQsMC4zLTAuNywwLjUtMS4xYzAuNy0xLjMsMS42LTIuNiwyLjctMy42YzAuNS0wLjUsMS0wLjksMS41LTEuM2MxLTAuNywyLTEuNCwzLjItMS44YzEuOC0wLjgsMy43LTEuMSw1LjgtMS4xCgkJCQlDNS42LDkyLDcuOCw5Mi41LDkuOCw5My41eiIvPgoJCTwvZz4KCQk8Zz4KCQkJPHBhdGggY2xhc3M9InN0MyIgZD0iTS0yNi42LDEyMy4zYy0wLjItMy4xLTIuNy01LjgtNS44LTYuM2MtMC4zLDAtMC41LTAuMS0wLjgtMC4xYy0wLjQsMC0wLjgsMC0xLjIsMGMtMC4yLDAtMC40LDAuMS0wLjYsMC4xCgkJCQljLTAuOSwwLjItMS43LDAuNi0yLjQsMWMtMS45LDEuMi0zLjEsMy40LTMuMSw1LjhjMCwzLjgsMy4xLDcsNyw3YzAuNywwLDEuMy0wLjEsMS45LTAuM2MwLjUtMC4yLDEtMC40LDEuNS0wLjYKCQkJCWMxLjEtMC42LDItMS41LDIuNi0yLjZjMC4xLTAuMSwwLjEtMC4yLDAuMi0wLjNDLTI2LjcsMTI1LjgtMjYuNSwxMjQuNi0yNi42LDEyMy4zeiIvPgoJCQk8cGF0aCBjbGFzcz0ic3Q0IiBkPSJNLTI3LjgsMTIzLjRjLTAuMi0yLjYtMi4yLTQuNy00LjctNS4yYy0wLjIsMC0wLjQtMC4xLTAuNy0wLjFzLTAuNywwLTEsMGMtMC4yLDAtMC4zLDAtMC41LDAuMQoJCQkJYy0wLjcsMC4yLTEuNCwwLjUtMiwwLjljLTEuNSwxLTIuNSwyLjctMi41LDQuN2MwLDMuMSwyLjUsNS43LDUuNyw1LjdjMC42LDAsMS4xLTAuMSwxLjYtMC4yYzAuNC0wLjEsMC44LTAuMywxLjItMC41CgkJCQljMC45LTAuNSwxLjYtMS4yLDIuMS0yLjFjMC0wLjEsMC4xLTAuMiwwLjEtMC4zQy0yOCwxMjUuNS0yNy43LDEyNC41LTI3LjgsMTIzLjR6Ii8+CgkJCTxwYXRoIGNsYXNzPSJzdDUiIGQ9Ik0tMjguOSwxMjMuNWMtMC4yLTIuMS0xLjgtMy45LTMuOS00LjJjLTAuMiwwLTAuNCwwLTAuNS0wLjFjLTAuMywwLTAuNSwwLTAuOCwwYy0wLjEsMC0wLjMsMC0wLjQsMC4xCgkJCQljLTAuNiwwLjEtMS4xLDAuNC0xLjYsMC43Yy0xLjIsMC44LTIuMSwyLjItMi4xLDMuOWMwLDIuNiwyLjEsNC43LDQuNyw0LjdjMC41LDAsMC45LTAuMSwxLjMtMC4yYzAuNC0wLjEsMC43LTAuMiwxLTAuNAoJCQkJYzAuNy0wLjQsMS4zLTEsMS43LTEuOGMwLTAuMSwwLjEtMC4xLDAuMS0wLjJDLTI5LDEyNS4yLTI4LjgsMTI0LjMtMjguOSwxMjMuNXoiLz4KCQkJPHBhdGggY2xhc3M9InN0NiIgZD0iTS0yOS44LDEyMy41Yy0wLjEtMS43LTEuNC0zLjEtMy4xLTMuNGMtMC4xLDAtMC4zLDAtMC40LDBjLTAuMiwwLTAuNCwwLTAuNiwwYy0wLjEsMC0wLjIsMC0wLjMsMC4xCgkJCQljLTAuNSwwLjEtMC45LDAuMy0xLjMsMC42Yy0xLDAuNy0xLjcsMS44LTEuNywzLjFjMCwyLjEsMS43LDMuOCwzLjgsMy44YzAuNCwwLDAuNy0wLjEsMS4xLTAuMmMwLjMtMC4xLDAuNi0wLjIsMC44LTAuMwoJCQkJYzAuNi0wLjMsMS4xLTAuOCwxLjQtMS40YzAtMC4xLDAuMS0wLjEsMC4xLTAuMkMtMjkuOCwxMjQuOS0yOS43LDEyNC4yLTI5LjgsMTIzLjV6Ii8+CgkJCTxwYXRoIGNsYXNzPSJzdDciIGQ9Ik0tMzAuNywxMjMuNmMtMC4xLTEuMy0xLjEtMi40LTIuNC0yLjZjLTAuMSwwLTAuMiwwLTAuMywwYy0wLjIsMC0wLjMsMC0wLjUsMGMtMC4xLDAtMC4yLDAtMC4zLDAKCQkJCWMtMC40LDAuMS0wLjcsMC4yLTEsMC40Yy0wLjgsMC41LTEuMywxLjQtMS4zLDIuNGMwLDEuNiwxLjMsMi44LDIuOCwyLjhjMC4zLDAsMC41LDAsMC44LTAuMWMwLjItMC4xLDAuNC0wLjIsMC42LTAuMwoJCQkJYzAuNC0wLjMsMC44LTAuNiwxLjEtMS4xYzAsMCwwLTAuMSwwLjEtMC4xQy0zMC43LDEyNC43LTMwLjYsMTI0LjEtMzAuNywxMjMuNnoiLz4KCQk8L2c+Cgk8L2c+Cgk8dGV4dCB0cmFuc2Zvcm09Im1hdHJpeCgxIDAgMCAxIDEyMS45NDc5IDE1MC41MDE1KSIgY2xhc3M9InN0OCBzdDkgc3QxMCI+aGVhcnRoIGZhaWx1cmU8L3RleHQ+CjwvZz4KPGc+Cgk8cGF0aCBjbGFzcz0ic3QxMSIgZD0iTTcuOSw3Mi42Yy0wLjIsMC0wLjQtMC4yLTAuNC0wLjNsLTAuMi0zLjNjMC0wLjIsMC4yLTAuMywwLjQtMC40YzAuMiwwLDAuNCwwLjIsMC40LDAuM2wwLjIsMy4zCgkJQzguMyw3Mi40LDguMSw3Mi42LDcuOSw3Mi42eiIvPgoJPHBhdGggY2xhc3M9InN0MTEiIGQ9Ik04LjYsNzguOEw4LjYsNzguOGwtMC4yLTIuN2MwLTAuMi0wLjItMC4zLTAuNC0wLjNjLTAuMiwwLTAuNCwwLjItMC40LDAuNGwwLjIsMy4zbDAsMGwwLjIsMi43CgkJYzAsMC4yLDAuMiwwLjMsMC40LDAuM2MwLjIsMCwwLjQtMC4yLDAuNC0wLjRMOC42LDc4Ljh6Ii8+Cgk8Zz4KCQk8Zz4KCQkJPGc+CgkJCQk8cGF0aCBjbGFzcz0ic3QxMSIgZD0iTTg4LjQsMTEuN2MtMC4yLTAuMS0wLjUsMC4xLTAuNSwwLjNsLTAuOCw0LjFjLTAuMSwwLjIsMC4xLDAuNSwwLjMsMC41YzAuMiwwLjEsMC41LTAuMSwwLjUtMC4zbDAuOC00LjEKCQkJCQlDODguNywxMiw4OC42LDExLjgsODguNCwxMS43eiIvPgoJCQkJPHBhdGggY2xhc3M9InN0MTEiIGQ9Ik04NywxOWMtMC4yLTAuMS0wLjUsMC4xLTAuNSwwLjNsLTAuNywzLjJsMCwwTDg1LDI2LjZjLTAuMSwwLjIsMC4xLDAuNSwwLjMsMC41YzAuMiwwLjEsMC41LTAuMSwwLjUtMC4zCgkJCQkJbDAuNy0zLjJsMCwwbDAuOC00LjFDODcuMywxOS4yLDg3LjEsMTksODcsMTl6Ii8+CgkJCTwvZz4KCQkJPHBhdGggY2xhc3M9InN0MTEiIGQ9Ik0xNDUuNyw4LjljMC4xLTAuMiwwLjMtMC40LDAuNS0wLjNsNC4xLDAuOGMwLjIsMC4xLDAuNCwwLjIsMC4zLDAuNWMtMC4xLDAuMi0wLjMsMC40LTAuNSwwLjNMMTQ2LDkuNAoJCQkJQzE0NS44LDkuMywxNDUuNyw5LjEsMTQ1LjcsOC45eiIvPgoJCQk8cGF0aCBjbGFzcz0ic3QxMiIgZD0iTTEzMy4xLDEwOC45TDgyLjYsOTguOGMtNS43LTEuMi05LjQtNi43LTguMi0xMi4zbDE2LTc3LjhjMS4yLTUuNiw2LjgtOS4zLDEyLjUtOC4ybDUwLjUsMTAuMQoJCQkJYzUuNywxLjIsOS40LDYuNyw4LjIsMTIuM2wtMTYsNzcuOEMxNDQuNCwxMDYuMywxMzguOCwxMTAsMTMzLjEsMTA4Ljl6Ii8+CgkJCTxwYXRoIGNsYXNzPSJzdDEzIiBkPSJNMTMxLjgsMTA4LjdMODEuNyw5OC41Yy01LjctMS4yLTkuNC02LjctOC4yLTEyLjNMODkuMyw4LjRjMS4yLTUuNiw2LjctOS4zLDEyLjQtOC4ybDUwLjIsMTAuMQoJCQkJYzUuNywxLjIsOS40LDYuNyw4LjIsMTIuM2wtMTUuOSw3Ny44QzE0My4xLDEwNi4yLDEzNy41LDEwOS45LDEzMS44LDEwOC43eiIvPgoJCQk8cGF0aCBjbGFzcz0ic3QxNCIgZD0iTTExNy4zLDkuMWwtMC4xLDAuNGMtMC4xLDAuNCwwLjIsMC43LDAuNSwwLjhsMTcuNiwzLjRjMC40LDAuMSwwLjctMC4yLDAuOC0wLjVsMC4xLTAuNAoJCQkJYzAuMS0wLjQtMC4yLTAuNy0wLjUtMC44TDExOCw4LjZDMTE3LjcsOC41LDExNy4zLDguOCwxMTcuMyw5LjF6Ii8+CgkJCTxwYXRoIGNsYXNzPSJzdDE0IiBkPSJNMTM4LDk4LjZMODAuNSw4Ny40Yy0xLjktMC40LTMuMi0yLjItMi44LTQuMWwxNC4yLTcyYzAuNC0xLjksMi4yLTMuMiw0LjEtMi44bDU3LjUsMTEuMgoJCQkJYzEuOSwwLjQsMy4yLDIuMiwyLjgsNC4xbC0xNC4yLDcyQzE0MS44LDk3LjgsMTM5LjksOTksMTM4LDk4LjZ6Ii8+CgkJCTxwYXRoIGNsYXNzPSJzdDE1IiBkPSJNNjEuMSwzMi4zaDU2LjVjMi44LDAsNSwyLjIsNSw1djMwLjRjMCwyLjgtMi4yLDUtNSw1aC00LjlsMC42LDIuN2MwLjIsMC45LDAuMSwyLTAuNSwyLjgKCQkJCWMtMC4xLDAuMS0wLjEsMC4yLTAuMiwwLjJjLTEuMywxLjYtMy44LDEuNy01LjMsMC4ybC01LjktNS45SDYxLjFjLTIuOCwwLTUtMi4yLTUtNVYzNy4zQzU2LjEsMzQuNiw1OC4zLDMyLjMsNjEuMSwzMi4zeiIvPgoJCTwvZz4KCTwvZz4KCTxwYXRoIGNsYXNzPSJzdDEyIiBkPSJNNDUuOSwxMzUuOUwyMi4zLDEzN2MtNS4zLDAuMy0xMC0zLjMtMTAuMy03LjlMNy42LDY1Yy0wLjMtNC42LDMuOC04LjcsOS4yLTguOUw0MC40LDU1CgkJYzUuMy0wLjMsMTAsMy4zLDEwLjMsNy45bDQuNCw2NEM1NS40LDEzMS42LDUxLjMsMTM1LjcsNDUuOSwxMzUuOXoiLz4KCTxwYXRoIGNsYXNzPSJzdDExIiBkPSJNMzYuNCw1NS4yYzAtMC4yLDAuMi0wLjUsMC41LTAuNWw0LjktMC4yYzAuMiwwLDAuNSwwLjIsMC41LDAuNHMtMC4yLDAuNS0wLjUsMC41bC00LjksMC4yCgkJQzM2LjcsNTUuNiwzNi40LDU1LjQsMzYuNCw1NS4yeiIvPgoJPHBhdGggY2xhc3M9InN0MTMiIGQ9Ik00Ni45LDEzNS4ybC0yMy42LDEuMWMtNS4zLDAuMy0xMC0zLjItMTAuMy03LjlMOC42LDY0LjljLTAuMy00LjYsMy44LTguNiw5LjItOC45TDQxLjQsNTUKCQljNS4zLTAuMywxMCwzLjIsMTAuMyw3LjlsNC40LDYzLjVDNTYuNCwxMzAuOSw1Mi4zLDEzNC45LDQ2LjksMTM1LjJ6Ii8+Cgk8cGF0aCBjbGFzcz0ic3QxNCIgZD0iTTIyLjYsNjAuNXYwLjNjMCwwLjMsMC4zLDAuNSwwLjYsMC41bDE2LjYtMC45YzAuMywwLDAuNi0wLjIsMC41LTAuNXYtMC4zYzAtMC4zLTAuMy0wLjUtMC42LTAuNWwtMTYuNiwwLjkKCQlDMjIuOSw1OS45LDIyLjYsNjAuMiwyMi42LDYwLjV6Ii8+Cgk8cGF0aCBjbGFzcz0ic3QxNCIgZD0iTTUyLDEyNmwtMzQuMiwyYy0wLjcsMC4xLTEuMy0wLjUtMS4zLTEuMmwtNC43LTYxLjZjLTAuMS0wLjcsMC41LTEuMywxLjItMS40bDM0LjItMgoJCWMwLjctMC4xLDEuMywwLjUsMS4zLDEuMmw0LjcsNjEuNkM1My4yLDEyNS4zLDUyLjcsMTI2LDUyLDEyNnoiLz4KCTxnIGNsYXNzPSJzdDE2Ij4KCQk8ZyBjbGFzcz0ic3QxNiI+CgkJCTxwYXRoIGNsYXNzPSJzdDE3IiBkPSJNNTQuNCw1OC41Yy0xNywwLjgtMzAuMSwxMS41LTI5LjUsMjMuN2MwLjMsNi40LDQuNCwxMS45LDEwLjYsMTUuNmwtMi4yLDYuNGMtMC40LDEuMS0wLjMsMi4zLDAuMiwzLjMKCQkJCWMwLjEsMC4xLDAuMiwwLjIsMC4yLDAuM2MxLjQsMiw0LjIsMi40LDYuMSwwLjhsNy44LTYuNGMyLjksMC41LDUuOSwwLjYsOC45LDAuNWMxNy0wLjgsMzAuMS0xMS41LDI5LjUtMjMuNwoJCQkJQzg1LjYsNjYuOCw3MS4zLDU3LjYsNTQuNCw1OC41eiIvPgoJCTwvZz4KCTwvZz4KCTxwYXRoIGNsYXNzPSJzdDE4IiBkPSJNNzguMiw1NC44YzEuOSwwLDMuNC0xLjUsMy40LTMuNHMtMS41LTMuNC0zLjQtMy40Yy0xLjksMC0zLjQsMS41LTMuNCwzLjRTNzYuMyw1NC44LDc4LjIsNTQuOHoiLz4KCTxwYXRoIGNsYXNzPSJzdDE4IiBkPSJNOTEuNiw1NC44YzEuOSwwLDMuNC0xLjUsMy40LTMuNHMtMS41LTMuNC0zLjQtMy40cy0zLjQsMS41LTMuNCwzLjRTODkuNyw1NC44LDkxLjYsNTQuOHoiLz4KCTxwYXRoIGNsYXNzPSJzdDE4IiBkPSJNMTA1LjEsNTQuOGMxLjksMCwzLjQtMS41LDMuNC0zLjRzLTEuNS0zLjQtMy40LTMuNHMtMy40LDEuNS0zLjQsMy40QzEwMS42LDUzLjMsMTAzLjIsNTQuOCwxMDUuMSw1NC44eiIvPgoJPHBhdGggY2xhc3M9InN0MTgiIGQ9Ik02NC4xLDc1LjNjMC01LTQuMS05LTktOXMtOS4xLDQtOS4xLDljMCwxLjIsMSwyLjMsMi4zLDIuM2MxLjIsMCwyLjMtMSwyLjMtMi4zYzAtMi41LDItNC41LDQuNS00LjUKCQljMi41LDAsNC41LDIsNC41LDQuNWMwLDIuNS0yLDQuNS00LjUsNC41Yy0xLjIsMC0yLjMsMS0yLjMsMi4zdjUuOGMwLDEuMiwxLDIuMywyLjMsMi4zYzEuMiwwLDIuMy0xLDIuMy0yLjN2LTMuOAoJCUM2MS4yLDgzLDY0LjEsNzkuNSw2NC4xLDc1LjN6Ii8+Cgk8cGF0aCBjbGFzcz0ic3QxOCIgZD0iTTU3LjIsOTMuN2MtMC4xLTAuMi0wLjItMC4zLTAuMi0wLjRjLTAuMS0wLjItMC4yLTAuMi0wLjMtMC40Yy0wLjEtMC4xLTAuMi0wLjItMC40LTAuMwoJCWMtMC4yLTAuMS0wLjItMC4yLTAuNC0wLjJjLTAuMi0wLjEtMC4zLTAuMS0wLjUtMC4yYy0wLjMtMC4xLTAuNi0wLjEtMC45LDBjLTAuMiwwLTAuMywwLjEtMC41LDAuMmMtMC4yLDAuMS0wLjMsMC4yLTAuNCwwLjIKCQljLTAuMiwwLjEtMC4yLDAuMi0wLjQsMC4zYy0wLjEsMC4xLTAuMiwwLjItMC4zLDAuNHMtMC4yLDAuMi0wLjIsMC40Yy0wLjEsMC4yLTAuMSwwLjMtMC4yLDAuNGMwLDAuMi0wLjEsMC4zLTAuMSwwLjUKCQljMCwwLjIsMCwwLjMsMC4xLDAuNWMwLDAuMiwwLjEsMC4zLDAuMiwwLjVjMC4xLDAuMiwwLjIsMC4zLDAuMiwwLjRjMC4xLDAuMiwwLjIsMC4yLDAuMywwLjRjMC4xLDAuMSwwLjIsMC4yLDAuNCwwLjMKCQljMC4yLDAuMSwwLjIsMC4yLDAuNCwwLjJjMC4yLDAuMSwwLjMsMC4xLDAuNSwwLjJjMC4yLDAsMC4zLDAuMSwwLjUsMC4xYzAuMiwwLDAuMywwLDAuNS0wLjFjMC4yLDAsMC4zLTAuMSwwLjUtMC4yCgkJYzAuMi0wLjEsMC4zLTAuMiwwLjQtMC4yYzAuMi0wLjEsMC4yLTAuMiwwLjQtMC4zYzAuMS0wLjEsMC4yLTAuMiwwLjMtMC40YzAuMS0wLjIsMC4yLTAuMiwwLjItMC40YzAuMS0wLjIsMC4xLTAuMywwLjItMC41CgkJYzAtMC4yLDAuMS0wLjMsMC4xLTAuNWMwLTAuMiwwLTAuMy0wLjEtMC41QzU3LjMsOTQsNTcuMiw5My44LDU3LjIsOTMuN3oiLz4KPC9nPgo8L3N2Zz4Koo',
      mainButtons: [
        { code: 'timeline', iconColor: '#647aec' },
        { code: 'dashboard', iconColor: '#2e2e2e', notifications: 1 },
        { code: 'measures', iconColor: '#faba8c' },
        { code: 'patient-events', iconColor: '#fc907b' }
      ],
      secondaryButtonGroups: [
        {
          title: 'Categories',
          buttons: [
            {
              label: 'Sam Category',
              type: 'category',
              href: 'https://dev-mobi-julien.bepatientsolutions.com/api/contents/categories/22654d690e04466ab3f82054a129304c'
            },
            {
              label: 'Form Container',
              type: 'forms-container',
              href: 'https://dev-mobi-julien.bepatientsolutions.com/api/contents/categories/729208a72661480ea698b93cbdacaf1f'
            },
            {
              label: 'Factsheet',
              type: 'fiche',
              href: 'https://dev-mobi-julien.bepatientsolutions.com/api/contents/factsheets/7978ed7d777d4fa1890c78f63693543a'
            }
          ]
        },
        {
          title: 'Forms',
          buttons: [
            {
              label: 'Multi-question',
              type: 'form',
              href: 'https://dev-mobi-julien.bepatientsolutions.com/api/contents/forms/bb68bc256ce04b6e8a79d20124e59f55'
            },
            {
              label: 'With HTML',
              type: 'form',
              href: 'https://dev-mobi-julien.bepatientsolutions.com/api/contents/forms/5c832d8941cf434fa039d3489f11f961'
            }
          ]
        },
        {
          title: 'Quizzes',
          buttons: [
            {
              label: 'Sam Quizz',
              type: 'quizz',
              href: 'https://dev-mobi-julien.bepatientsolutions.com/api/contents/quizzes/01887dc1ed434985be30cadc879ec954'
            },
            {
              label: 'Alex quizz',
              type: 'quizz',
              href: 'https://dev-mobi-julien.bepatientsolutions.com/api/contents/quizzes/349d32d4cf854b189c90705d6a9a00e2'
            }
          ]
        },
        {
          title: 'Chatbots',
          buttons: [
            {
              label: 'Chatbot',
              type: 'chatbot',
              href: 'https://dev-mobi-julien.bepatientsolutions.com/api/chatbot/barometer'
            }
          ]
        },
        {
          title: 'Body Schemas',
          buttons: [
            {
              label: 'Body Schema',
              type: 'body-schema-pain',
              href: 'https://dev-mobi-julien.bepatientsolutions.com/api/contents/body-schema-pain/7d652b548a124a4da434be637d802704'
            }
          ]
        }
      ]
    });
  }

  /**
   * @ignore
   */
  private mockedMenu(): CoreMenu {
    return {
      color: '#647aec',
      instanceLogo:
        // tslint:disable-next-line:max-line-length
        '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',
      mainButtons: [
        { id: 'home', iconColor: '#647aec' },
        { id: 'messages', iconColor: '#2e2e2e', notifications: 1 },
        { id: 'myMeasures', iconColor: '#faba8c' },
        { id: 'events', iconColor: '#fc907b' }
      ],
      secondaryButtonGroups: [
        {
          title: 'Vos fiches pratiques',
          buttons: [
            {
              label: 'Lins. cardiaque chronique',
              resource: void 0
            },
            { label: 'Troubles du rythme', resource: void 0 },
            {
              label:
                'Ceci est un test pour le dépassement du nombre de caratères',
              resource: void 0
            },
            { label: 'Messages hebdo.', resource: void 0 }
          ]
        }
      ]
    };
  }
}
